import type { Skill } from '../skillsData';

// ===== Identity / DID / Reputation extra (from skills/identity/) =====

export const cryptoskillIdentityExtraData: Skill[] = [
  {
    id: 'id-8004-mcp',
    name: '8004-mcp',
    author: 'ERC-8004 Foundation',
    authorHandle: '@erc8004',
    tag: 'Skill',
    category: 'tools',
    description: 'ERC-8004 Model Context Protocol server. Expose agent identity operations via MCP — register, resolve, verify, and manage agent identities for any MCP-compatible client.',
    downloads: 8400,
    stars: 420,
    version: '1.0.0',
    installCommand: '> install the 8004-mcp skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/identity/8004-mcp',
    usageExample: '> register agent via MCP\n> resolve DID via MCP\n> verify identity via MCP',
    detailedDescription: 'ERC-8004 MCP server providing standardized Model Context Protocol interfaces for agent identity management. Register, resolve, update, and verify agent identities from any MCP-compatible AI client or IDE.',
    trending: true,
  },
  {
    id: 'id-8004-skill-monad',
    name: '8004-skill-monad',
    author: 'Monad Labs',
    authorHandle: '@monad_xyz',
    tag: 'Skill',
    category: 'tools',
    description: 'ERC-8004 agent identity on Monad. High-throughput agent registration and reputation management on the Monad L1 with 10,000 TPS finality.',
    downloads: 6200,
    stars: 310,
    version: '1.0.0',
    installCommand: '> install the 8004-skill-monad skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/identity/8004-skill-monad',
    usageExample: '> register agent on Monad\n> query Monad reputation\n> bridge identity to Monad',
    detailedDescription: 'ERC-8004 identity deployment on Monad L1 leveraging its 10,000 TPS throughput and 1-second finality for high-frequency agent registration, reputation updates, and cross-chain identity bridging.',
  },
  {
    id: 'id-8004-skill',
    name: '8004-skill',
    author: 'ERC-8004 Foundation',
    authorHandle: '@erc8004',
    tag: 'Skill',
    category: 'tools',
    description: 'ERC-8004 standard skill library. Core primitives for agent identity, capability manifests, reputation scores, and on-chain attestation workflows.',
    downloads: 9200,
    stars: 460,
    version: '1.0.0',
    installCommand: '> install the 8004-skill skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/identity/8004-skill',
    usageExample: '> create identity primitive\n> publish capability manifest\n> attest reputation score',
    detailedDescription: 'ERC-8004 core skill library exposing identity primitives, capability manifest publishing, reputation score computation, and on-chain attestation workflows for building custom agent identity solutions.',
  },
  {
    id: 'id-8004scan-webhooks',
    name: '8004scan-webhooks',
    author: 'ERC-8004 Foundation',
    authorHandle: '@erc8004',
    tag: 'Skill',
    category: 'analytics',
    description: '8004scan webhook integration. Subscribe to real-time identity events — new registrations, reputation changes, capability updates, and trust attestations via webhooks.',
    downloads: 5800,
    stars: 290,
    version: '1.0.0',
    installCommand: '> install the 8004scan-webhooks skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/identity/8004scan-webhooks',
    usageExample: '> subscribe to identity events\n> listen for reputation changes\n> webhook on new registration',
    detailedDescription: '8004scan real-time webhook service for monitoring ERC-8004 identity events. Receive push notifications for agent registrations, reputation score updates, capability manifest changes, and cross-chain trust attestations.',
  },
  {
    id: 'aegis-verify-identity',
    name: 'aegis-verify-identity',
    author: 'Aegis Protocol',
    authorHandle: '@aegis_protocol',
    tag: 'Skill',
    category: 'tools',
    description: 'Aegis identity verification suite. Comprehensive KYC/AML verification for AI agents with document verification, biometric checks, and on-chain attestation issuance.',
    downloads: 7600,
    stars: 380,
    version: '1.0.0',
    installCommand: '> install the aegis-verify-identity skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/identity/aegis-verify-identity',
    usageExample: '> verify agent documents\n> run biometric check\n> issue attestation',
    detailedDescription: 'Aegis comprehensive identity verification suite combining document verification (passport, ID), biometric matching, liveness detection, and on-chain attestation issuance for KYC/AML-compliant AI agents.',
  },
  {
    id: 'agent-consensus-kudos',
    name: 'agent-consensus-kudos',
    author: 'Consensus Labs',
    authorHandle: '@consensus_labs',
    tag: 'Skill',
    category: 'tools',
    description: 'Agent consensus kudos system. Decentralized peer review where agents endorse each other with weighted kudos backed by stake, creating a Sybil-resistant reputation graph.',
    downloads: 5400,
    stars: 270,
    version: '1.0.0',
    installCommand: '> install the agent-consensus-kudos skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/identity/agent-consensus-kudos',
    usageExample: '> issue kudos to agent\n> query kudos graph\n> calculate reputation score',
    detailedDescription: 'Decentralized agent endorsement system using stake-weighted kudos. Agents issue kudos to peers backed by staked tokens, creating a Sybil-resistant directed graph for computing decentralized reputation scores.',
  },
  {
    id: 'agent8-reputation-economy',
    name: 'agent8-reputation-economy',
    author: 'Agent8',
    authorHandle: '@agent8_xyz',
    tag: 'Skill',
    category: 'tools',
    description: 'Agent8 reputation economy. Tokenized reputation marketplace where agents trade, stake, and monetize their reputation scores as liquid reputation NFTs.',
    downloads: 8200,
    stars: 410,
    version: '1.0.0',
    installCommand: '> install the agent8-reputation-economy skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/identity/agent8-reputation-economy',
    usageExample: '> mint reputation NFT\n> trade reputation token\n> stake for boost',
    detailedDescription: 'Agent8 tokenized reputation economy enabling agents to mint reputation as NFTs, trade reputation scores in a marketplace, stake reputation for access boosts, and liquidate earned trust into fungible tokens.',
  },
  {
    id: 'agentproof-trust-oracle',
    name: 'agentproof-trust-oracle',
    author: 'AgentProof',
    authorHandle: '@agentproof_io',
    tag: 'Skill',
    category: 'tools',
    description: 'AgentProof trust oracle. Cryptographic proof-of-execution oracle that verifies agent task completion and issues on-chain trust attestations without human intermediaries.',
    downloads: 6800,
    stars: 340,
    version: '1.0.0',
    installCommand: '> install the agentproof-trust-oracle skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/identity/agentproof-trust-oracle',
    usageExample: '> submit execution proof\n> verify task completion\n> issue trust attestation',
    detailedDescription: 'AgentProof decentralized trust oracle using cryptographic proof-of-execution. Agents submit verifiable computation proofs for completed tasks; the oracle validates and issues on-chain trust attestations automatically.',
  },
  {
    id: 'id-base-8004',
    name: 'base-8004',
    author: 'Base',
    authorHandle: '@base',
    tag: 'Skill',
    category: 'tools',
    description: 'Base ERC-8004 identity layer. Native ERC-8004 agent identity registry optimized for Base L2 with Coinbase Wallet integration and Base-specific reputation primitives.',
    downloads: 11400,
    stars: 570,
    version: '1.0.0',
    installCommand: '> install the base-8004 skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/identity/base-8004',
    usageExample: '> register agent on Base\n> link Coinbase Wallet\n> use Base reputation',
    detailedDescription: 'ERC-8004 identity layer native to Base L2 with Coinbase Wallet native integration, Base Attestation Station compatibility, low-cost identity registration, and Base-specific reputation primitives.',
  },
  {
    id: 'id-doppel-erc-8004',
    name: 'doppel-erc-8004',
    author: 'Doppel',
    authorHandle: '@doppel_xyz',
    tag: 'Skill',
    category: 'tools',
    description: 'Doppel ERC-8004 impersonation detection. Monitor and detect duplicate or impersonating agent identities across the ERC-8004 registry using similarity analysis and on-chain forensics.',
    downloads: 4800,
    stars: 240,
    version: '1.0.0',
    installCommand: '> install the doppel-erc-8004 skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/identity/doppel-erc-8004',
    usageExample: '> scan for duplicates\n> detect impersonators\n> flag suspicious agent',
    detailedDescription: 'Doppel impersonation detection for ERC-8004 agent identities. Uses metadata similarity analysis, behavioral fingerprinting, and on-chain forensics to identify and flag duplicate or malicious impersonating agents.',
  },
  {
    id: 'id-emmet-lukso',
    name: 'emmet-lukso',
    author: 'Emmet',
    authorHandle: '@emmet_app',
    tag: 'Skill',
    category: 'tools',
    description: 'Emmet LUKSO bridge. Bridge agent identities between ERC-8004 and LUKSO Universal Profiles, enabling cross-protocol identity portability and unified reputation.',
    downloads: 5600,
    stars: 280,
    version: '1.0.0',
    installCommand: '> install the emmet-lukso skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/identity/emmet-lukso',
    usageExample: '> bridge to LUKSO\n> sync reputation\n> migrate identity',
    detailedDescription: 'Emmet cross-protocol identity bridge connecting ERC-8004 agent identities with LUKSO Universal Profiles. Bidirectional sync of identity metadata, reputation scores, and capability manifests across both standards.',
  },
  {
    id: 'id-erc-8004',
    name: 'erc-8004',
    author: 'ERC-8004 Foundation',
    authorHandle: '@erc8004',
    tag: 'Skill',
    category: 'tools',
    description: 'ERC-8004 standard specification skill. Full implementation of the ERC-8004 agent identity standard with DID resolution, capability manifests, and reputation attestation contracts.',
    downloads: 10400,
    stars: 520,
    version: '1.0.0',
    installCommand: '> install the erc-8004 skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/identity/erc-8004',
    usageExample: '> deploy ERC-8004 contract\n> resolve agent DID\n> publish manifest',
    detailedDescription: 'Complete ERC-8004 standard reference implementation including smart contract suite for agent identity registration, DID resolution, capability manifest storage, reputation attestation, and cross-chain identity bridging.',
    trending: true,
  },
  {
    id: 'id-erc8004-discover',
    name: 'erc8004-discover',
    author: 'ERC-8004 Foundation',
    authorHandle: '@erc8004',
    tag: 'Skill',
    category: 'analytics',
    description: 'ERC-8004 agent discovery engine. Search, filter, and browse registered AI agents by capability, reputation, network, and author with advanced query capabilities.',
    downloads: 7200,
    stars: 360,
    version: '1.0.0',
    installCommand: '> install the erc8004-discover skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/identity/erc8004-discover',
    usageExample: '> search agents by capability\n> filter by reputation\n> browse trending agents',
    detailedDescription: 'Advanced ERC-8004 agent discovery engine supporting full-text search, capability-based filtering, reputation range queries, network-specific browsing, and trending agent leaderboards.',
  },
  {
    id: 'id-erc8004-register',
    name: 'erc8004-register',
    author: 'ERC-8004 Foundation',
    authorHandle: '@erc8004',
    tag: 'Skill',
    category: 'tools',
    description: 'ERC-8004 registration toolkit. Streamlined agent onboarding with automated identity deployment, ENS subdomain allocation, and initial reputation bootstrapping.',
    downloads: 8800,
    stars: 440,
    version: '1.0.0',
    installCommand: '> install the erc8004-register skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/identity/erc8004-register',
    usageExample: '> onboard new agent\n> allocate ENS subdomain\n> bootstrap reputation',
    detailedDescription: 'Streamlined ERC-8004 agent onboarding toolkit. Automated smart contract deployment, ENS subdomain registration, initial reputation bootstrapping via social graph import, and capability manifest generation.',
  },
  {
    id: 'foru-gatekeeper',
    name: 'foru-gatekeeper',
    author: 'Foru Protocol',
    authorHandle: '@foru_protocol',
    tag: 'Skill',
    category: 'tools',
    description: 'Foru identity gatekeeper. Role-based access control for agent communities with reputation-gated permissions, tiered membership, and dynamic credential verification.',
    downloads: 6200,
    stars: 310,
    version: '1.0.0',
    installCommand: '> install the foru-gatekeeper skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/identity/foru-gatekeeper',
    usageExample: '> create access tier\n> gate by reputation\n> verify credentials',
    detailedDescription: 'Foru Protocol decentralized access control for agent communities. Create reputation-gated tiers, assign dynamic permissions, verify credentials on-demand, and manage role-based community membership.',
  },
  {
    id: 'freak-v1',
    name: 'freak-v1',
    author: 'Freak Protocol',
    authorHandle: '@freak_proto',
    tag: 'Skill',
    category: 'tools',
    description: 'Freak Protocol v1 identity attestations. Privacy-preserving agent identity proofs using FHE (fully homomorphic encryption) for verifiable computation on encrypted identity data.',
    downloads: 4200,
    stars: 210,
    version: '1.0.0',
    installCommand: '> install the freak-v1 skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/identity/freak-v1',
    usageExample: '> create FHE identity\n> prove encrypted attribute\n> verify without decrypt',
    detailedDescription: 'Freak Protocol v1 using fully homomorphic encryption for privacy-preserving agent identity. Prove attributes and reputation scores without revealing underlying data — enabling verifiable computation on encrypted identity.',
  },
  {
    id: 'identity-verify-lit',
    name: 'identity-verify-lit',
    author: 'Lit Protocol',
    authorHandle: '@litprotocol',
    tag: 'Skill',
    category: 'tools',
    description: 'Lit Protocol identity verification. Decentralized key management for agent identities — programmable signing, access control via PKPs, and cross-chain identity delegation.',
    downloads: 7800,
    stars: 390,
    version: '1.0.0',
    installCommand: '> install the identity-verify-lit skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/identity/identity-verify-lit',
    usageExample: '> create PKP identity\n> set access conditions\n> delegate signing',
    detailedDescription: 'Lit Protocol decentralized key management for AI agent identities. Create Programmable Key Pairs (PKPs), configure access control conditions, enable cross-chain signing delegation, and manage identity key sharding.',
  },
  {
    id: 'lit-identity-verify',
    name: 'lit-identity-verify',
    author: 'Lit Protocol',
    authorHandle: '@litprotocol',
    tag: 'Skill',
    category: 'tools',
    description: 'Lit identity verification alternate. Additional Lit Protocol identity workflows — threshold signing, time-locked access, and conditional credential presentation.',
    downloads: 6400,
    stars: 320,
    version: '1.0.0',
    installCommand: '> install the lit-identity-verify skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/identity/lit-identity-verify',
    usageExample: '> threshold sign identity\n> time-lock credentials\n> conditional presentation',
    detailedDescription: 'Extended Lit Protocol identity verification workflows including threshold signature schemes for multi-party identity control, time-locked credential access, and conditional attribute presentation using Lit Actions.',
  },
  {
    id: 'maiat-guard',
    name: 'maiat-guard',
    author: 'MaIAT',
    authorHandle: '@maiat_io',
    tag: 'Skill',
    category: 'tools',
    description: 'MaIAT Guard identity firewall. Real-time monitoring and protection of agent identities with anomaly detection, impersonation alerts, and automatic reputation quarantine.',
    downloads: 5200,
    stars: 260,
    version: '1.0.0',
    installCommand: '> install the maiat-guard skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/identity/maiat-guard',
    usageExample: '> monitor identity health\n> detect anomalies\n> quarantine suspicious agent',
    detailedDescription: 'MaIAT Guard real-time identity protection system. Monitors agent identity behavior for anomalies, issues impersonation alerts, automatically quarantines suspicious identities, and initiates reputation recovery workflows.',
  },
  {
    id: 'openstoa-agent',
    name: 'openstoa-agent',
    author: 'OpenStoa',
    authorHandle: '@openstoa',
    tag: 'Skill',
    category: 'tools',
    description: 'OpenStoa open reputation. Transparent and auditable agent reputation system with public reputation ledgers, open-source scoring algorithms, and community governance.',
    downloads: 5800,
    stars: 290,
    version: '1.0.0',
    installCommand: '> install the openstoa-agent skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/identity/openstoa-agent',
    usageExample: '> query public reputation\n> audit scoring algorithm\n> vote on parameter change',
    detailedDescription: 'OpenStoa transparent reputation infrastructure. Public on-chain reputation ledgers, fully open-source scoring algorithms, community governance for parameter changes, and third-party audit compatibility.',
  },
  {
    id: 'worldcoin-official-agentkit',
    name: 'worldcoin-official-agentkit',
    author: 'Worldcoin',
    authorHandle: '@worldcoin',
    tag: 'Skill',
    category: 'tools',
    description: 'Worldcoin official AgentKit. Integrate World ID proof-of-personhood for AI agents — verify unique humanity, prevent Sybil attacks, and build human-verified agent ecosystems.',
    downloads: 22400,
    stars: 1120,
    version: '1.0.0',
    installCommand: '> install the worldcoin-official-agentkit skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/identity/worldcoin-official-agentkit',
    usageExample: '> verify with World ID\n> check proof-of-personhood\n> prevent Sybil agents',
    detailedDescription: 'Official Worldcoin AgentKit integrating World ID proof-of-personhood for AI agents. Verify unique human identity, prevent Sybil attacks, and build human-verified agent ecosystems with Orb and phone attestations.',
    featured: true,
    trending: true,
  },
  {
    id: 'zkproofport-agent',
    name: 'zkproofport-agent',
    author: 'ZKProofPort',
    authorHandle: '@zkproofport',
    tag: 'Skill',
    category: 'tools',
    description: 'ZKProofPort zero-knowledge identity. Prove agent attributes and reputation without revealing underlying data — age, compliance, skill levels via zk-SNARK credentials.',
    downloads: 8600,
    stars: 430,
    version: '1.0.0',
    installCommand: '> install the zkproofport-agent skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/identity/zkproofport-agent',
    usageExample: '> prove age in ZK\n> verify compliance proof\n> skill credential ZK proof',
    detailedDescription: 'ZKProofPort zero-knowledge identity system for AI agents. Generate and verify zk-SNARK proofs for attributes like age, compliance status, skill levels, and reputation without revealing underlying sensitive data.',
    trending: true,
  },
];